/* Performance workspace — P&L, ratios, execution quality (spec §11, §12, §14).
 *
 * What a trader needs to answer "how am I doing, and why".
 *
 * Two things this panel does that a normal analytics screen does not, and both
 * matter more than any chart on it:
 *
 *   A METRIC THAT COULD NOT BE COMPUTED SHOWS "—", NEVER ZERO. A win rate over
 *   no trades is undefined; a Sharpe of 0.00 is a real and meaningful result.
 *   Rendering the first as the second puts a plausible number on an empty
 *   account, and that is exactly the figure someone screenshots.
 *
 *   EVERY NUMBER CAN SHOW ITS WORKING. The API returns a methodology block with
 *   each response and it is displayed, not discarded. A Sharpe ratio without its
 *   period and risk-free assumption is not something anyone can act on.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { Panel } from '@/components/Panel'
import { fmtMoney, fmtSigned } from '@/lib/format'
import { Empty, SignedBars, type BarDatum } from '@/components/viz'

const PERIODS = ['today', 'week', 'month', 'quarter', 'fy', 'all'] as const
type Period = (typeof PERIODS)[number]

export function Performance() {
  const [period, setPeriod] = useState<Period>('month')
  const [pnl, setPnl] = useState<Record<string, any> | null>(null)
  const [perf, setPerf] = useState<Record<string, any> | null>(null)
  const [exec, setExec] = useState<Record<string, any> | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [showMethod, setShowMethod] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [p, f, e] = await Promise.allSettled([
      api.pnl({ period }),
      api.performance({ period }),
      api.executionAnalytics({ period }),
    ])
    if (p.status === 'fulfilled') setPnl(p.value)
    if (f.status === 'fulfilled') setPerf(f.value)
    if (e.status === 'fulfilled') setExec(e.value)
    setFailed([p, f, e].every((r) => r.status === 'rejected'))
    setLoading(false)
  }, [period])

  useEffect(() => { void load() }, [load])

  if (loading && !perf) {
    return <Panel title="Performance"><Empty>Loading…</Empty></Panel>
  }
  if (failed) {
    return (
      <Panel title="Performance">
        <Empty>Analytics are unavailable — the API could not be reached.</Empty>
      </Panel>
    )
  }

  const curve: { key: string; label: string; value: number }[] =
    (perf?.equity_curve ?? []).map((d: any) => ({
      key: d.date, label: d.date, value: d.cumulative,
    }))

  const symbolBars: BarDatum[] = (perf?.by_symbol ?? [])
    .filter((s: any) => Number.isFinite(s.net))
    .map((s: any) => ({ key: s.symbol, label: s.symbol, value: s.net }))

  return (
    <Panel
      title="Performance"
      actions={
        <div style={{ display: 'flex', gap: 'var(--sp-1)' }}>
          {PERIODS.map((p) => (
            <button key={p} onClick={() => setPeriod(p)} aria-pressed={period === p}
              style={{
                padding: '1px var(--sp-2)',
                background: period === p ? 'var(--accent-bg)' : 'transparent',
                border: `1px solid ${period === p ? 'var(--accent)' : 'var(--border)'}`,
                color: period === p ? 'var(--accent)' : 'var(--text-muted)',
                borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-2xs)',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {p}
            </button>
          ))}
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>

        {/* --- P&L headline --- */}
        {pnl && (
          <Section title={`Profit and loss · ${pnl.from_date} → ${pnl.to_date}`}>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: 'var(--sp-3)',
            }}>
              <Hero label="Net P&L" value={fmtSigned(pnl.net_pnl)}
                    tone={Number(pnl.net_pnl) >= 0 ? 'profit' : 'loss'} />
              <Hero label="Realised" value={fmtSigned(pnl.realized_pnl)}
                    tone={Number(pnl.realized_pnl) >= 0 ? 'profit' : 'loss'} />
              <Hero label="Unrealised (mark)" value={fmtSigned(pnl.unrealized_pnl)}
                    tone={Number(pnl.unrealized_pnl) >= 0 ? 'profit' : 'loss'} />
              <Hero label="Charges" value={fmtMoney(pnl.charges?.total, 'INR', true)} />
              <Hero label="Turnover" value={fmtMoney(pnl.turnover, 'INR', true)} />
            </div>

            {/* Unrealised is a MARK, not a result. Saying so beside the number
                is what stops a good day on paper reading as a realised one. */}
            <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 6 }}>
              Unrealised is a mark against the last price — it moves when the
              market does and is not a realised result.
            </div>

            {pnl.charges && (
              <div className="scroll-x" style={{ marginTop: 'var(--sp-3)' }}>
                <table className="qx">
                  <thead>
                    <tr>
                      <th>Charge</th><th className="r">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(pnl.charges)
                      .filter(([k]) => k !== 'total')
                      .map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}</td>
                          <td className="num r">{fmtMoney(v as string)}</td>
                        </tr>
                      ))}
                    <tr>
                      <td style={{ fontWeight: 700 }}>Total</td>
                      <td className="num r" style={{ fontWeight: 700 }}>
                        {fmtMoney(pnl.charges.total)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}

        {/* --- ratios --- */}
        {perf && (
          <Section title={`Metrics · ${perf.trading_days} trading day(s), ${perf.fills} fill(s)`}>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))',
              gap: 'var(--sp-2)',
            }}>
              <Metric label="Win rate" value={perf.win_rate_pct} suffix="%" />
              <Metric label="Profit factor" value={perf.profit_factor} dp={2} />
              <Metric label="Expectancy" value={perf.expectancy} money />
              <Metric label="Sharpe" value={perf.sharpe_ratio} dp={2} />
              <Metric label="Sortino" value={perf.sortino_ratio} dp={2} />
              <Metric label="Max drawdown" value={perf.max_drawdown} money tone="loss" />
              <Metric label="Avg win" value={perf.average_win} money tone="profit" />
              <Metric label="Avg loss" value={perf.average_loss} money tone="loss" />
              <Metric label="Largest win" value={perf.largest_win} money tone="profit" />
              <Metric label="Largest loss" value={perf.largest_loss} money tone="loss" />
              <Metric label="Fill rate" value={perf.orders?.fill_rate_pct} suffix="%" />
              <Metric label="Reject rate" value={perf.orders?.reject_rate_pct}
                      suffix="%" tone="loss" />
            </div>

            {perf.trading_days === 0 && (
              <div style={{
                marginTop: 'var(--sp-2)', padding: 'var(--sp-2)',
                background: 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 9, color: 'var(--text-muted)', lineHeight: 1.5,
              }}>
                No completed trading day in this period, so every ratio above is
                shown as <b>—</b>. That is "not computable", not zero: a win rate
                over no trades is undefined, and rendering it as 0% would put a
                real-looking figure on an empty account.
              </div>
            )}
          </Section>
        )}

        {/* --- equity curve --- */}
        {curve.length > 1 && (
          <Section title="Cumulative P&L">
            <SignedBars
              data={curve.map((c) => ({ key: c.key, label: c.label, value: c.value }))}
              format={(v) => fmtSigned(v)}
              maxRows={30}
            />
          </Section>
        )}

        {/* --- attribution --- */}
        {symbolBars.length > 0 && (
          <Section title="Net by symbol">
            <SignedBars data={symbolBars} format={(v) => fmtSigned(v)} maxRows={20} />
          </Section>
        )}

        {/* --- execution quality --- */}
        {exec && (
          <Section title="Execution quality">
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))',
              gap: 'var(--sp-2)',
            }}>
              <Metric label="Orders" value={exec.orders} dp={0} />
              <Metric label="Fill rate" value={exec.fill_rate_pct} suffix="%" />
              <Metric label="Qty fill rate" value={exec.quantity_fill_rate_pct} suffix="%" />
              <Metric label="Order→fill p50"
                      value={exec.order_to_fill_ms?.p50} suffix=" ms" dp={0} />
              <Metric label="Order→fill p99"
                      value={exec.order_to_fill_ms?.p99} suffix=" ms" dp={0} />
              <Metric label="Slippage (mean)"
                      value={exec.slippage_bps?.mean} suffix=" bps" dp={2} />
            </div>

            {exec.reject_causes?.length > 0 && (
              <div className="scroll-x" style={{ marginTop: 'var(--sp-3)' }}>
                <table className="qx">
                  <thead><tr><th>Rejection cause</th><th className="r">Count</th></tr></thead>
                  <tbody>
                    {exec.reject_causes.map((r: any) => (
                      <tr key={r.code}>
                        <td style={{ color: 'var(--loss)' }}>{r.code}</td>
                        <td className="num r">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}

        {/* --- how every number was calculated --- */}
        <details open={showMethod} onToggle={(e) => setShowMethod(e.currentTarget.open)}>
          <summary style={{
            cursor: 'pointer', fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700,
          }}>
            How these numbers are calculated
          </summary>
          <div style={{ marginTop: 'var(--sp-2)' }}>
            {[['P&L', pnl?.methodology], ['Metrics', perf?.methodology],
              ['Execution', exec?.methodology]].map(([title, m]) =>
              m ? (
                <div key={String(title)} style={{ marginBottom: 'var(--sp-3)' }}>
                  <div style={{
                    fontSize: 'var(--fs-2xs)', fontWeight: 700,
                    color: 'var(--text-secondary)', marginBottom: 3,
                  }}>
                    {String(title)}
                  </div>
                  {Object.entries(m as Record<string, string>).map(([k, v]) => (
                    <div key={k} style={{
                      fontSize: 9, color: 'var(--text-muted)',
                      lineHeight: 1.5, marginBottom: 3,
                    }}>
                      <b style={{ color: 'var(--text-secondary)' }}>
                        {k.replace(/_/g, ' ')}:
                      </b>{' '}
                      {v}
                    </div>
                  ))}
                </div>
              ) : null)}
          </div>
        </details>
      </div>
    </Panel>
  )
}

/** One metric. A null value renders as an em dash — it means "not computable
 *  from this sample", which is a different statement from zero. */
function Metric({ label, value, suffix = '', dp = 2, money, tone }: {
  label: string
  value: number | null | undefined
  suffix?: string
  dp?: number
  money?: boolean
  tone?: 'profit' | 'loss'
}) {
  const known = value != null && Number.isFinite(value)
  const text = !known ? '—'
    : money ? fmtSigned(value as number)
    : `${(value as number).toFixed(dp)}${suffix}`

  const color = !known ? 'var(--text-muted)'
    : tone === 'profit' ? 'var(--profit)'
    : tone === 'loss' ? 'var(--loss)'
    : 'var(--text-primary)'

  return (
    <div style={{
      padding: 'var(--sp-2)', background: 'var(--bg-input)',
      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    }}
      title={known ? undefined : 'Not computable from the available sample — this is not zero.'}
    >
      <div style={{
        fontSize: 9, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        {label}
      </div>
      <div className="num" style={{ fontSize: 'var(--fs-md)', fontWeight: 700, color }}>
        {text}
      </div>
    </div>
  )
}

function Hero({ label, value, tone }: {
  label: string; value: string; tone?: 'profit' | 'loss'
}) {
  return (
    <div style={{
      padding: 'var(--sp-2) var(--sp-3)',
      background: 'var(--bg-input)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </div>
      <div className="num" style={{
        fontSize: 'var(--fs-lg)', fontWeight: 700,
        color: tone === 'profit' ? 'var(--profit)'
             : tone === 'loss' ? 'var(--loss)' : 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 style={{
        fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: 'var(--text-muted)',
        marginBottom: 'var(--sp-2)', paddingBottom: 3,
        borderBottom: '1px solid var(--border)',
      }}>
        {title}
      </h3>
      {children}
    </section>
  )
}
